import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import CloseIcon from '@mui/icons-material/Close';
import {Box, Grid, IconButton, SxProps, Typography, useMediaQuery, useTheme} from '@mui/material';

import {DreamEmployerExpandableInfo} from './DreamEmployerExpandableInfo';
import {DreamEmployerGraph} from './DreamEmployerGraph/DreamEmployerGraph';
import {graphSerializer} from './DreamEmployerGraph/graphSerializer';
import {isLegacyDreamEmployer} from './DreamEmployerUtils/isLegacyDreamEmployer';
import {ComparisonCard} from '../../../../../../components/ComparisonCard/ComparisonCard';
import {LanguageContext} from '../../../../../../contexts/LanguageContext/LanguageContext';
import {greenColors, orangeColors} from '../../../../../../layouts/theme/designSystem';
import {DreamEmployerClass, DreamEmployerLegacyClass} from '../../types/DreamEmployerTypes';

interface Props {
  dreamEmployerData: DreamEmployerClass | DreamEmployerLegacyClass;
  handleClose: () => void;
}

export const DreamEmployerModal = ({dreamEmployerData, handleClose}: Props) => {
  const {t} = useTranslation();
  const theme = useTheme();
  const currentLanguage = useContext(LanguageContext);
  const employeesLabel = t('companySurveys.stats.employees');

  const legendStyles: SxProps = {
    color: greenColors[100],
    fontSize: {xs: '17px', sm: '20px'},
    ml: {xs: 1, sm: 2},
  };

  const legendIconStyles: SxProps = {
    width: '8px',
    height: '30px',
    borderRadius: 2,
  };

  const isXs = useMediaQuery(theme.breakpoints.only('xs'));

  const getVariant = () => {
    if (isXs) return 'h3';
    return 'h2';
  };

  return (
    <Grid container>
      <Grid item container justifyContent={'space-between'} alignItems={'center'} mb={1}>
        <Grid item xs={11}>
          <Typography variant='h4'>{t('companyCultureDashboard.employeeSatisfaction.modal.title')}</Typography>
        </Grid>
        <Grid item xs={1} display={'flex'} justifyContent={'flex-end'}>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>
      <Typography variant={getVariant()} mb={1}>
        {!isLegacyDreamEmployer(dreamEmployerData)
          ? dreamEmployerData.headline[currentLanguage]
          : t('companyCultureDashboard.employeeSatisfaction.modal.totalOverlap', {
            totalOverlap: dreamEmployerData?.totalScore,
          })}
      </Typography>
      {!isLegacyDreamEmployer(dreamEmployerData) && (
        <Typography mb={1} sx={{color: greenColors[700]}}>
          {dreamEmployerData.text[currentLanguage]}
        </Typography>
      )}

      {!isLegacyDreamEmployer(dreamEmployerData) && <DreamEmployerExpandableInfo />}

      {isLegacyDreamEmployer(dreamEmployerData) && (
        <Box marginTop={4}>
          <Grid item container mb={1} flexWrap={'nowrap'}>
            <Grid item sx={legendIconStyles} bgcolor={greenColors[100]} />
            <Typography variant='body1' sx={legendStyles}>
              {t('companyCultureDashboard.employeeSatisfaction.modal.aboutCompany')}{' '}
            </Typography>
          </Grid>
          <Grid item container flexWrap={'nowrap'}>
            <Grid item sx={legendIconStyles} bgcolor={orangeColors[100]} />
            <Typography variant='body1' sx={legendStyles}>
              {t('companyCultureDashboard.employeeSatisfaction.modal.aboutDreamEmployer')}{' '}
            </Typography>
          </Grid>
        </Box>
      )}
      <Grid item mt={3} xs={12}>
        {!isLegacyDreamEmployer(dreamEmployerData)
          ? dreamEmployerData?.questions.map((item, index) => (
            <DreamEmployerGraph
              key={`${item.title[currentLanguage]}-${index}`}
              title={item.title[currentLanguage]}
              leftLabel={item.leftOption[currentLanguage]}
              rightLabel={item.rightOption[currentLanguage]}
              chartData={graphSerializer(item, employeesLabel)}
            />
          ))
          : dreamEmployerData?.questions.map((question, index) => (
            <ComparisonCard
              key={index}
              methodItem={question.methodItem}
              firstIndicatorValue={question.results.valueDreamEmployer || 0}
              secondIndicatorValue={question.results.valueEmployees}
              overlap={question.results.overlap}
              bubbleSize={40}
            />
          ))}
      </Grid>
    </Grid>
  );
};
